import React from "react";
import ReactDOM from "react-dom";
import { NavLink } from "react-router-dom";
import { m, LazyMotion, domAnimation, AnimatePresence } from "framer-motion";
import { OnboardingSlide } from "core/models/OnboardingSlide";
import Button from "ui/components/atoms/Button";
import { cx } from "utils/join-classnames";
import $ from "./Onboarding.module.scss";

interface Props {
    slides: OnboardingSlide[];
    onComplete(): void;
}

const Onboarding: React.FC<Props> = ({ slides, onComplete }) => {
    const [activeSlideIdx, setActiveSlideIdx] = React.useState<number>(0);
    const nextSlide = (): void => {
        setActiveSlideIdx((prevValue) => prevValue + 1);
    };

    return ReactDOM.createPortal(
        <div className={$.container}>
            {slides.map(
                (slide, index) =>
                    index === activeSlideIdx && (
                        <LazyMotion
                            features={domAnimation}
                            strict
                            key={slide.id}
                        >
                            <AnimatePresence mode="wait">
                                <m.div
                                    initial={{ x: 100, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: -100 }}
                                    key={slide.id}
                                    className={$.slide}
                                >
                                    <img src={slide.image} alt={slide.title} />
                                    <h2 className={$.slide__title}>
                                        {slide.title}
                                    </h2>
                                    <div className={$.slide__body}>
                                        {slide.body}
                                    </div>
                                    <div className={$.slide__actions}>
                                        {slide.contactUs && (
                                            <NavLink
                                                to="/support"
                                                className={$.contact}
                                            >
                                                Contact us
                                            </NavLink>
                                        )}
                                        <Button
                                            theme="dark"
                                            type="primary"
                                            onClick={
                                                index === slides.length - 1
                                                    ? onComplete
                                                    : nextSlide
                                            }
                                        >
                                            {slide.buttonText}
                                        </Button>
                                    </div>
                                </m.div>
                            </AnimatePresence>
                        </LazyMotion>
                    ),
            )}
            {slides.length > 1 && (
                <div className={$.bullets}>
                    {slides.map((slide, index) => (
                        <div
                            className={cx(
                                $.bullet,
                                index === activeSlideIdx ? $.active : undefined,
                            )}
                            key={slide.id}
                            onClick={() => setActiveSlideIdx(index)}
                            role="presentation"
                        />
                    ))}
                </div>
            )}
            <div className={$.background} />
        </div>,
        document.body,
    );
};

export default Onboarding;
